import type { AuditEntry, HitlActionType } from '../services/types';

export interface AuditTrailProps {
  entries: AuditEntry[];
  /** Optional loading indicator while the trail is being fetched. */
  loading?: boolean;
  /** Shown when the list is empty and not loading. */
  emptyMessage?: string;
  /** Compact layout — drop the ruling lines and tighten spacing. */
  dense?: boolean;
}

/**
 * Timeline of reviewer actions on a hit.
 *
 * Rendered inside the ReviewerModal and exposed as a standalone component so
 * Stage 4's run-level audit log can reuse it unchanged.
 */
export function AuditTrail({ entries, loading, emptyMessage, dense }: AuditTrailProps) {
  if (loading && entries.length === 0) {
    return <p className="text-xs text-muted">Loading audit trail…</p>;
  }
  if (entries.length === 0) {
    return (
      <p className="text-xs text-muted italic">
        {emptyMessage ?? 'No reviewer actions yet.'}
      </p>
    );
  }

  // Oldest first for readability (it's a timeline).
  const sorted = [...entries].sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));

  return (
    <ol
      aria-label="Audit trail"
      className={dense ? 'space-y-1.5' : 'space-y-3 border-l border-border pl-4'}
    >
      {sorted.map((e) => (
        <li key={e.audit_id} className="text-xs">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className={`inline-block rounded-sm px-1.5 py-0.5 font-semibold uppercase tracking-wide ${actionStyles[e.action_type]}`}
            >
              {actionLabel[e.action_type]}
            </span>
            <span className="text-ink font-medium">{e.reviewer_id}</span>
            <time className="text-muted" dateTime={e.timestamp}>
              {formatTime(e.timestamp)}
            </time>
          </div>
          {e.comment && <p className="mt-1 text-ink/90">{e.comment}</p>}
          {e.payload && Object.keys(e.payload).length > 0 && (
            <pre className="mt-1 font-mono text-muted bg-surface-alt/30 rounded px-2 py-1 overflow-x-auto">
              {JSON.stringify(e.payload)}
            </pre>
          )}
        </li>
      ))}
    </ol>
  );
}

const actionStyles: Record<HitlActionType, string> = {
  choose_format: 'bg-brand/15 text-brand-dark',
  choose_format_spec: 'bg-brand/15 text-brand-dark',
  no_format_required: 'bg-brand/15 text-brand-dark',
  accept: 'bg-brand/15 text-brand-dark',
  escalate: 'bg-warn/15 text-warn',
  rerun_chunk: 'bg-surface-alt text-ink',
  rerun_full: 'bg-surface-alt text-ink',
  // v0.4 Stage C — rule confirmation actions reuse the brand colour
  // family because they're constructive reviewer decisions.
  confirm_rule: 'bg-brand/15 text-brand-dark',
  modify_rule: 'bg-brand/15 text-brand-dark',
  reject_rule: 'bg-warn/15 text-warn',
  // v0.4 Stage D.5 — validator threshold actions. clear is destructive
  // (warn family); tag is informational (brand family).
  clear_failing_cells: 'bg-warn/15 text-warn',
  tag_for_review: 'bg-brand/15 text-brand-dark',
  // v0.5 Stage C.2 — reviewer confirmed a multi-type column.
  choose_mixed_types: 'bg-brand/15 text-brand-dark',
  // v0.5 Stage E.1 — reviewer re-opened a confirmed/rejected rule.
  revisit_rule: 'bg-warn/15 text-warn',
  // v0.6 Stage G.3 — reviewer re-opened a cleanser-quality decision.
  // Same warn-family colour as revisit_rule because the semantics are
  // identical (invalidates a prior decision rather than confirming a
  // new one).
  revisit_cleanser_decision: 'bg-warn/15 text-warn',
  // v0.5 Stage F.4 — reviewer picked a target unit for conversion.
  choose_unit_target: 'bg-brand/15 text-brand-dark',
  // v0.5 Stage F.2 Phase 2 — reviewer picked a JSON flatten mode.
  choose_json_flatten_mode: 'bg-brand/15 text-brand-dark',
  // v0.5 Stage F.3 — reviewer picked cadence + interpolation method.
  choose_series_cadence: 'bg-brand/15 text-brand-dark',
  // v0.5 Stage F.1 — reviewer picked address expansion mode.
  choose_address_format: 'bg-brand/15 text-brand-dark',
  // v0.6 Stage H.2 — reviewer confirmed (and optionally renamed)
  // a multi-date column split.
  choose_multi_date_split: 'bg-brand/15 text-brand-dark',
  // v0.5 Stage G.3 — reviewer picked PDF tables to extract.
  choose_pdf_tables: 'bg-brand/15 text-brand-dark',
  // v0.6 Stage B6 — reviewer confirmed which header-matched columns
  // should remain blurred in the upload preview.
  confirm_header_pii: 'bg-brand/15 text-brand-dark',
  // v0.6 Stage B12 — QualityGateCard sub-panel resolutions for the
  // missing_handler HIGH_MISSING_THRESHOLD HITL. Constructive reviewer
  // decisions; same brand colour as the other choose_* actions.
  switch_to_mode: 'bg-brand/15 text-brand-dark',
  switch_to_constant: 'bg-brand/15 text-brand-dark',
};

const actionLabel: Record<HitlActionType, string> = {
  choose_format: 'choose',
  choose_format_spec: 'pick format',
  no_format_required: 'no format',
  accept: 'accept',
  escalate: 'escalate',
  rerun_chunk: 'rerun chunk',
  rerun_full: 'rerun all',
  // v0.4 Stage C
  confirm_rule: 'confirm rule',
  modify_rule: 'modify rule',
  reject_rule: 'reject rule',
  // v0.4 Stage D.5
  clear_failing_cells: 'clear cells',
  tag_for_review: 'tag for review',
  // v0.5 Stage C.2
  choose_mixed_types: 'mixed types',
  // v0.5 Stage E.1
  revisit_rule: 'revisit rule',
  // v0.6 Stage G.3
  revisit_cleanser_decision: 'revisit decision',
  // v0.5 Stage F.4
  choose_unit_target: 'pick unit',
  // v0.5 Stage F.2 Phase 2
  choose_json_flatten_mode: 'pick flatten',
  // v0.5 Stage F.3
  choose_series_cadence: 'pick cadence',
  // v0.5 Stage F.1
  choose_address_format: 'pick address',
  // v0.6 Stage H.2
  choose_multi_date_split: 'split dates',
  // v0.5 Stage G.3
  choose_pdf_tables: 'pick pdf tables',
  // v0.6 Stage B6
  confirm_header_pii: 'confirm blur',
  // v0.6 Stage B12 — short labels for the QualityGateCard sub-panel
  // resolutions, used in the audit trail timeline.
  switch_to_mode: 'fill (mode)',
  switch_to_constant: 'fill (constant)',
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    // HH:MM:SS on the day, else full ISO-ish. Reviewers usually care about
    // "how long ago" at glance; full date is in the dateTime attr.
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}
